import { RefreshCwIcon } from "lucide-react";
import { cloneDeep, debounce, get, set } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FormFactory from "@/components/modules/FormFactory/FormFactory";
import { FormFieldsProvider } from "@/components/modules/FormFactory/FormFieldsContext";
import { useMountEffect } from "@/hooks/useMountEffect";
import { FormType, hasError } from "@/integrations";

import { Button } from "../elements/Button";

const formToFilteredArray = <T,>(
  form: FormType,
  filterFn: (form: FormType) => boolean,
  mapFn: (form: FormType) => T,
): T[] => {
  if (!form) return [];
  const filteredFields: T[] = [];
  if (filterFn(form)) filteredFields.push(mapFn(form));

  if ("fields" in form)
    return (
      (form.fields as FormType[])?.reduce<T[]>(
        (prev, field) => [
          ...prev,
          ...formToFilteredArray<T>(field, filterFn, mapFn),
        ],
        filteredFields,
      ) || []
    );

  return filteredFields;
};

const findDependentFields = (fieldName: string, form: FormType): FormType[] =>
  formToFilteredArray<FormType>(
    form,
    (form) =>
      "dependsOnFieldsWithNames" in form &&
      !!form.dependsOnFieldsWithNames?.includes(fieldName),
    (form) => form,
  );

const hasAnyError = (form: FormType): boolean =>
  formToFilteredArray<FormType>(form, hasError, (form) => form).length > 0;

const getDefaultValues = (
  form: FormType,
  acc: Record<string, any> = {},
): Record<string, any> => {
  switch (form.type) {
    case "sub_form":
      if (!form.required) {
        // If the subform is not required, it has a value on its own
        set(acc, form.name, get(acc, form.name) ?? Boolean(form.value));
      }
      form.fields?.forEach((field) => {
        getDefaultValues(field, acc);
      });
      return acc;
    case "repeater":
      form.value?.forEach((subForm) => {
        getDefaultValues(subForm, acc);
      });
      return acc;
    case "text":
    case "email":
    case "number":
    case "password":
    case "checkbox":
    case "hidden":
      if (form.value) {
        set(acc, form.name, get(acc, form.name) ?? form.value);
      }
      return acc;
  }
  return acc;
};

export default function Custom(props: {
  onChange: (setting: Record<string, any>) => void;
  form: FormType;
  fetchSettings: (formValues: Record<string, any>, forceFetch?: boolean) => any;
  isFetching?: boolean;
  onChangeValid: (isValid: boolean) => void;
  initialValues?: Record<string, any>;
  refetchSettingsOnLoad?: boolean;
}) {
  const methods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: props.initialValues || {},
  });

  const { fetchSettings } = props;
  const { getValues, setValue } = methods;

  const allFields = useMemo(
    () =>
      formToFilteredArray<FormType>(
        props.form,
        () => true,
        (f) => f,
      ),
    [props.form],
  );

  const handleFetchServerDataDebounced = useMemo(
    () => debounce(() => fetchSettings(getValues()), 400),
    [fetchSettings, getValues],
  );

  const handleFieldChanged = useCallback(
    (name: string) => {
      const values = getValues();
      const defaultValues = getDefaultValues(props.form, values);

      const dependentFields = findDependentFields(name, props.form);

      dependentFields.forEach(async (field) => {
        if (!field.name) {
          return;
        }
        // reset fields
        if (values[field.name]) {
          setValue(field.name, defaultValues[field.name]);
        }
      });
    },
    [getValues, setValue, props.form],
  );

  useMountEffect(() => {
    // if this component mounts, start out by making custom settings invalid
    props.onChangeValid(false);
    if (props.refetchSettingsOnLoad) {
      handleFetchServerDataDebounced();
    }
    const defaultValues = getDefaultValues(props.form, props.initialValues);
    props.onChange(defaultValues);
  });

  useEffect(() => {
    props.onChangeValid(methods.formState.isValid && !hasAnyError(props.form));
  }, [methods.formState.isValid, props]);

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (!name) return;

      if (shouldFetchConnectionSettings(props.form, name)) {
        handleFetchServerDataDebounced();
      }
      methods.trigger().then(() => {
        /**
         * Normally, input field name is primitive `account-id`
         * and we find it within a form value with `value[name]`:
         * `{ account-id: "1", account-key: "2"} => "1"`.
         *
         * Repeaters use standard HTTP naming scheme for lists
         * with input names like `accounts[0][id]`. Whereas the form value
         * will be properly nested: `{accounts: [ {id: "marko"} ]}`.
         */
        const [keyName] = name.split("[", 1);
        // setValue mutates nested values in place; a snapshot lets consumers see the change.
        props.onChange(cloneDeep({ ...value, [keyName]: get(value, keyName) }));
      });
      handleFieldChanged(name);
    });

    return () => subscription.unsubscribe();
  }, [
    handleFetchServerDataDebounced,
    handleFieldChanged,
    methods,
    props,
    getValues,
  ]);

  return (
    <FormProvider {...methods}>
      <FormFieldsProvider fields={allFields}>
        <div className="mb-2 flex justify-end">
          <Button
            size="xs"
            variant="ghost"
            onClick={() => fetchSettings(getValues(), true)}
            icon={<RefreshCwIcon />}
            isLoading={props.isFetching}
            loadingText="Fetching settings..."
          >
            Refetch settings
          </Button>
        </div>
        <FormFactory form={props.form} />
      </FormFieldsProvider>
    </FormProvider>
  );
}

/**
 * This is a temporary solution to prevent fetching connection settings
 * for certain fields. The current setup is not very flexible and will
 * fetch connection settings for every change in the form.
 */
function shouldFetchConnectionSettings(form: FormType, fieldName: string) {
  if (form.type === "google-ads-form" && fieldName === "custom_reports") {
    return false;
  }
  return true;
}
